// A short three-note chime for a coupon arriving, synthesised with the Web Audio API — no
// audio file, so no asset to ship and no `media-src` CSP entry to keep in sync.
//
// The sound is a supplement, never the only signal: browsers keep an AudioContext suspended
// until the page has had a user gesture, and when it can't start we stay silent rather than
// queue a chime that would sound minutes later, detached from the arrival it announced.

type AudioContextCtor = new () => AudioContext;

/** Arrivals closer together than this share one chime. */
export const CHIME_BURST_MS = 3000;
/** If the context hasn't resumed by now, this chime is skipped, not deferred. */
const RESUME_GRACE_MS = 400;

// Three rising notes, a little longer and fuller than a UI click, so it carries over a busy room.
// Triangle rather than sine adds odd harmonics, which read as louder at the same peak without
// the harshness of a square wave. 0.22 peak is well clear of clipping with three overlapping tails.
const NOTES: { hz: number; at: number }[] = [
	{ hz: 659.25, at: 0 }, // E5
	{ hz: 880, at: 0.11 }, // A5
	{ hz: 1318.51, at: 0.22 }, // E6
];
const NOTE_LENGTH = 0.32;
const PEAK_GAIN = 0.22;
const WAVE: OscillatorType = 'triangle';

let context: AudioContext | null = null;
let lastChimeAt = Number.NEGATIVE_INFINITY;

const audioContextCtor = (): AudioContextCtor | undefined => {
	if (typeof window === 'undefined') return undefined;
	// Read off window rather than the global, so a browser without Web Audio yields undefined
	// instead of a ReferenceError. Older Safari only has the prefixed constructor.
	const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
	return w.AudioContext ?? w.webkitAudioContext;
};

const ring = (ctx: AudioContext) => {
	const start = ctx.currentTime;
	NOTES.forEach(({ hz, at }) => {
		const oscillator = ctx.createOscillator();
		const gain = ctx.createGain();
		oscillator.type = WAVE;
		oscillator.frequency.value = hz;
		gain.gain.setValueAtTime(0, start + at);
		gain.gain.linearRampToValueAtTime(PEAK_GAIN, start + at + 0.015);
		gain.gain.exponentialRampToValueAtTime(0.0001, start + at + NOTE_LENGTH);
		oscillator.connect(gain).connect(ctx.destination);
		oscillator.start(start + at);
		oscillator.stop(start + at + NOTE_LENGTH + 0.02);
	});
};

/** Plays the arrival chime unless one just played. Never throws. */
export const playCouponChime = (now: number = Date.now()): void => {
	if (now - lastChimeAt < CHIME_BURST_MS) return;
	lastChimeAt = now;

	try {
		const Ctor = audioContextCtor();
		if (!Ctor) return;
		context ??= new Ctor();
		const ctx = context;

		if (ctx.state === 'running') {
			ring(ctx);
			return;
		}
		const asked = Date.now();
		ctx
			.resume()
			.then(() => {
				if (ctx.state === 'running' && Date.now() - asked <= RESUME_GRACE_MS) ring(ctx);
			})
			.catch(() => {
				// No user gesture yet: stay silent. The ribbon itself is the notification.
			});
	} catch {
		// Audio unavailable on this device; nothing to do.
	}
};

/** Test seam: forget the previous chime so bursts can be asserted from a clean slate. */
export const resetCouponChimeForTests = () => {
	lastChimeAt = Number.NEGATIVE_INFINITY;
	context = null;
};
